import React from 'react';
import {
  Package, Store, Users, ShoppingBag,
  ChevronRight, Megaphone, BarChart3, Layers,
  ArrowUpRight, ArrowDownRight, Wrench
} from 'lucide-react';

const formatSEK = (n) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', minimumFractionDigits: 0 }).format(n || 0);

const TrendBadge = ({ value }) => {
  if (value === 0 || value === undefined) return <span className="text-xs text-gray-400">—</span>;
  const isUp = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${isUp ? 'text-emerald-600' : 'text-red-500'}`} data-testid="trend-badge">
      {isUp ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {isUp ? '+' : ''}{value}%
    </span>
  );
};

const PremiumStatCard = ({ icon: Icon, label, value, trend, color, onClick }) => {
  const colorStyles = {
    blue: { bg: 'bg-blue-50', icon: 'bg-blue-500', text: 'text-blue-600' },
    emerald: { bg: 'bg-emerald-50', icon: 'bg-emerald-500', text: 'text-emerald-600' },
    violet: { bg: 'bg-violet-50', icon: 'bg-violet-500', text: 'text-violet-600' },
    amber: { bg: 'bg-amber-50', icon: 'bg-amber-500', text: 'text-amber-600' },
  };
  const s = colorStyles[color] || colorStyles.blue;

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-2xl p-5 text-left transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 border border-gray-100 group cursor-pointer w-full"
      data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl ${s.icon} flex items-center justify-center shadow-sm`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <TrendBadge value={trend} />
      </div>
      <p className="text-3xl font-black text-[#0F172A] tracking-tight">{value}</p>
      <div className="flex items-center justify-between mt-1">
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
      </div>
    </button>
  );
};

const TopListItem = ({ rank, name, value, maxValue }) => {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-2.5">
      <span className="text-xs font-bold text-gray-400 w-5 text-right">{rank}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-gray-800 truncate">{name}</span>
          <span className="text-sm font-semibold text-gray-700 ml-2 flex-shrink-0">{formatSEK(value)}</span>
        </div>
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#003B73] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};

export const OverviewSection = ({ overview, settings, trends, topStores, topCategories, pendingBanners, onNavigate, maintenanceMode }) => (
  <div className="space-y-8" data-testid="admin-overview">
    {/* Maintenance banner */}
    {maintenanceMode && (
      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3" data-testid="maintenance-banner">
        <Wrench className="h-5 w-5 text-amber-600 flex-shrink-0" />
        <p className="text-sm font-medium text-amber-800">Underhallslagt aktivt — Sidan ar dold for besokare</p>
        <button onClick={() => onNavigate('settings')} className="ml-auto text-sm font-semibold text-amber-700 hover:underline">Installningar</button>
      </div>
    )}

    {/* Pending actions */}
    {(pendingBanners > 0 || (overview.pending_stores || 0) > 0) && (
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex flex-wrap gap-4" data-testid="pending-actions">
        {(overview.pending_stores || 0) > 0 && (
          <button onClick={() => onNavigate('stores')} className="flex items-center gap-2 text-sm font-medium text-orange-700 hover:underline">
            <Store className="h-4 w-4" /> {overview.pending_stores} butik{overview.pending_stores > 1 ? 'er' : ''} väntar <ChevronRight className="h-4 w-4" />
          </button>
        )}
        {pendingBanners > 0 && (
          <button onClick={() => onNavigate('ads')} className="flex items-center gap-2 text-sm font-medium text-orange-700 hover:underline">
            <Megaphone className="h-4 w-4" /> {pendingBanners} annons{pendingBanners > 1 ? 'er' : ''} väntar <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    )}

    {/* Stat cards */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
      <PremiumStatCard icon={Package} label="Produkter" value={overview.products || 0} trend={trends?.products} color="blue" onClick={() => onNavigate('products')} />
      <PremiumStatCard icon={Store} label="Butiker" value={overview.approved_stores || 0} trend={trends?.stores} color="emerald" onClick={() => onNavigate('stores')} />
      <PremiumStatCard icon={Users} label="Anvandare" value={overview.users || 0} trend={trends?.users} color="violet" onClick={() => onNavigate('customers')} />
      <PremiumStatCard icon={ShoppingBag} label="Ordrar" value={overview.orders || 0} trend={trends?.orders} color="amber" onClick={() => onNavigate('orders')} />
    </div>

    {/* Top lists */}
    <div className="grid md:grid-cols-2 gap-5">
      <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="top-stores-list">
        <div className="flex items-center gap-2 mb-5">
          <BarChart3 className="h-5 w-5 text-[#003B73]" />
          <h3 className="font-bold text-[#0F172A]">Omsattning per butik</h3>
        </div>
        {topStores && topStores.length > 0 ? (
          <div className="space-y-0.5">
            {topStores.map((s, i) => (
              <TopListItem key={s.store_id} rank={i + 1} name={s.store_name} value={s.total_sales} maxValue={topStores[0]?.total_sales || 1} />
            ))}
          </div>
        ) : <p className="text-sm text-gray-400">Ingen data annu</p>}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-6" data-testid="top-categories-list">
        <div className="flex items-center gap-2 mb-5">
          <Layers className="h-5 w-5 text-[#003B73]" />
          <h3 className="font-bold text-[#0F172A]">Omsattning per kategori</h3>
        </div>
        {topCategories && topCategories.length > 0 ? (
          <div className="space-y-0.5">
            {topCategories.map((c, i) => (
              <TopListItem key={c.category_id || i} rank={i + 1} name={c.category_name} value={c.total_sales} maxValue={topCategories[0]?.total_sales || 1} />
            ))}
          </div>
        ) : <p className="text-sm text-gray-400">Ingen data annu</p>}
      </div>
    </div>
  </div>
);
